import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, Card, Badge, OverlayTrigger, Tooltip, Form, Modal, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { format } from 'date-fns';
import ExcelJS from 'exceljs';
import { globalSearchTrips, getClients, getDrivers, getTripLogs, getCostCenters, getAreas, getVehicles, getBases, getRequesters } from '../api';
import TripAuditModal from './TripAuditModal';
import SearchableMultiSelect from './SearchableMultiSelect';
import { Clock, Plane, ShieldCheck, Calendar, Search, RotateCcw } from 'lucide-react';
import { toast } from 'react-toastify';

const COUNTRY_NAMES = {
    'CL': 'Chile',
    'AR': 'Argentina',
    'DO': 'Rep. Dominicana',
    'UY': 'Uruguay',
    'PE': 'Perú',
    'CO': 'Colombia',
    'BR': 'Brasil',
    'MX': 'México',
    'EC': 'Ecuador',
    'BO': 'Bolivia',
    'VE': 'Venezuela'
};

const getFlagEmoji = (timezone, localCurrencyCode, lat, lng, originBase = null) => {
    let tz = timezone;
    if (!tz) {
        if (localCurrencyCode === 'PEN') tz = 'America/Lima';
        else if (localCurrencyCode === 'ARS') tz = 'America/Argentina/Mendoza';
        else if (localCurrencyCode === 'EUR') tz = 'Europe/Madrid';
        else if (localCurrencyCode === 'UYU') tz = 'America/Montevideo';
        else if (lat < 0 && lat > -18 && lng < -68 && lng > -82) tz = 'America/Lima';
        else if (lat >= -35.5 && lat <= -30.0 && lng >= -58.5 && lng <= -53.0) tz = 'America/Montevideo';
        else if (lat >= 36.0 && lat <= 44.0 && lng >= -9.5 && lng <= 4.5) tz = 'Europe/Madrid';
        else tz = 'America/Santiago';
    }
    const tzLower = (tz || '').toLowerCase();
    let countryCode = originBase?.country_code ? originBase.country_code.toLowerCase().trim() : '';

    if (!countryCode) {
        if (tzLower.includes('montevideo') || tzLower.includes('uruguay')) countryCode = 'uy';
        else if (tzLower.includes('madrid') || tzLower.includes('barcelona') || tzLower.includes('spain')) countryCode = 'es';
        else if (tzLower.includes('santiago')) countryCode = 'cl';
        else if (tzLower.includes('argentina') || tzLower.includes('mendoza') || tzLower.includes('buenos_aires')) countryCode = 'ar';
        else if (tzLower.includes('lima')) countryCode = 'pe';
        else if (tzLower.includes('santo_domingo') || tzLower.includes('punta_cana')) countryCode = 'do';
        else if (tzLower.includes('bogota') || tzLower.includes('colombia')) countryCode = 'co';
        else if (tzLower.includes('new_york')) countryCode = 'us';
        else if (tzLower.includes('london')) countryCode = 'gb';
        else if (tzLower.includes('sao_paulo') || tzLower.includes('rio')) countryCode = 'br';
        else if (localCurrencyCode === 'EUR') countryCode = 'es';
        else if (localCurrencyCode === 'UYU') countryCode = 'uy';
        else if (lat >= -35.5 && lat <= -30.0 && lng >= -58.5 && lng <= -53.0) countryCode = 'uy';
        else if (lat >= 36.0 && lat <= 44.0 && lng >= -9.5 && lng <= 4.5) countryCode = 'es';
        else countryCode = 'cl';
    }

    return (
        <img 
            src={`https://flagcdn.com/w20/${countryCode}.png`} 
            srcSet={`https://flagcdn.com/w40/${countryCode}.png 2x`}
            width="20" 
            alt={countryCode.toUpperCase()}
            className="align-middle me-1 shadow-sm"
            style={{ borderRadius: '2px', border: '1px solid rgba(0,0,0,0.15)' }}
        />
    );
};

const STATUS_LABELS = {
    'pending': 'Pendiente', 'PENDING': 'Pendiente',
    'assigned': 'Asignado', 'ASSIGNED': 'Asignado',
    'confirmed': 'Confirmado', 'CONFIRMED': 'Confirmado',
    'en_route': 'En Camino', 'EN_ROUTE': 'En Camino',
    'arrived': 'En Posición', 'ARRIVED': 'En Posición',
    'on_board': 'En Ruta', 'ON_BOARD': 'En Ruta',
    'completed': 'Completado', 'COMPLETED': 'Completado',
    'cancelled': 'Cancelado', 'CANCELLED': 'Cancelado',
    'no_show': 'No Show', 'NO_SHOW': 'No Show',
    'anulado': 'Anulado', 'ANULADO': 'Anulado'
};

const getStatusColor = (status, isCritical) => {
    if (isCritical) return '#dc3545';
    switch (status?.toLowerCase()) {
        case 'pending': return '#ffc107';
        case 'assigned': return '#17a2b8';
        case 'confirmed': return '#007bff';
        case 'en_route': return '#fd7e14';
        case 'arrived': return '#6f42c1';
        case 'on_board': return '#28a745';
        case 'completed': return '#20c997';
        case 'cancelled': return '#6c757d';
        case 'no_show': return '#e83e8c';
        case 'anulado': return '#343a40';
        default: return '#6c757d';
    }
};

const isAirport = (address) => {
    if (!address || typeof address !== 'string') return false;
    const lower = address.toLowerCase();
    if (lower.includes('aeropuerto') || lower.includes('airport') || lower.includes('carriel sur') || lower.includes('el tepual') || lower.includes('chacalluta') || lower.includes('el plumerillo') || lower.includes('ezeiza') || lower.includes('pudahuel')) {
        return true;
    }
    const iataCodes = ['scl', 'anf', 'cjc', 'iqq', 'lsc', 'ccp', 'pmc', 'zco', 'ari', 'mdz', 'eze', 'aep', 'puj', 'sdq', 'lim', 'bog', 'cpo', 'araucania'];
    const iataRegex = new RegExp(`\\b(${iataCodes.join('|')})\\b`, 'i');
    return iataRegex.test(address);
};

const formatLogDate = (dateStr) => {
    if (!dateStr) return '---';
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '---';
        return format(d, 'dd/MM/yyyy HH:mm:ss');
    } catch (e) {
        return '---';
    }
};

const VehicleTypeIcon = ({ type }) => {
    const name = type?.name?.toLowerCase() || '';
    let icon = '🚗';
    let label = 'S';
    let fullName = 'Standard';

    if (name.includes('sprinter')) { icon = '🚐'; label = 'SPR'; fullName = 'Sprinter'; }
    else if (name.includes('large')) { icon = '🚐'; label = 'LPC'; fullName = 'Large People Carrier'; }
    else if (name.includes('people')) { icon = '🚐'; label = 'PC'; fullName = 'People Carrier'; }
    else if (name.includes('bus')) { icon = '🚌'; label = 'BUS'; fullName = 'Minibus / Bus'; }
    else if (name.includes('luxury')) { icon = '💎'; label = 'LUX'; fullName = 'Luxury'; }
    else if (name.includes('executive')) { icon = '⭐'; label = 'EXE'; fullName = 'Executive'; }

    return (
        <OverlayTrigger placement="top" overlay={<Tooltip>{fullName}</Tooltip>}>
            <div className="d-flex flex-column align-items-center" style={{lineHeight: 1, cursor: 'help'}}>
                <span style={{fontSize: '1.2rem'}}>{icon}</span>
                <small className="fw-bold text-muted" style={{fontSize: '0.65rem'}}>{label}</small>
            </div>
        </OverlayTrigger>
    );
};

const GlobalTripsView = ({ isLoaded }) => {
    // Maestros de filtros
    const [clients, setClients] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [costCenters, setCostCenters] = useState([]);
    const [areas, setAreas] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [bases, setBases] = useState([]);
    const [requesters, setRequesters] = useState([]);

    // Filtros seleccionados
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedBases, setSelectedBases] = useState([]);

    const countryOptions = useMemo(() => {
        const codes = [...new Set(bases.map(b => b.country_code).filter(Boolean).map(c => c.trim().toUpperCase()))];
        return codes.map(c => ({
            id: c,
            name: COUNTRY_NAMES[c] ? `${COUNTRY_NAMES[c]} (${c})` : `País (${c})`
        }));
    }, [bases]);

    const baseOptions = useMemo(() => {
        const filtered = selectedCountries.length > 0 
            ? bases.filter(b => b.country_code && selectedCountries.includes(b.country_code.trim().toUpperCase())) 
            : bases;
        return filtered.map(b => ({
            id: b.id.toString(),
            name: b.name
        }));
    }, [bases, selectedCountries]);

    // Filtros seleccionados
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [selectedClient, setSelectedClient] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState([]);
    const [selectedRequesters, setSelectedRequesters] = useState([]);
    const [filterStatus, setFilterStatus] = useState([]);
    const [selectedCostCenters, setSelectedCostCenters] = useState([]);
    const [selectedAreas, setSelectedAreas] = useState([]);
    const [passengerName, setPassengerName] = useState('');
    const [vehicleCode, setVehicleCode] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [tripIdFilter, setTripIdFilter] = useState('');
    const [osIdFilter, setOsIdFilter] = useState('');
    const [fileFilter, setFileFilter] = useState('');
    const [filterBillingStatus, setFilterBillingStatus] = useState('');

    const areaOptions = useMemo(() => {
        let filtered = areas.filter(a => a.is_active !== false);
        if (selectedClient.length > 0) {
            filtered = filtered.filter(a => selectedClient.includes(a.client_id));
        }
        return filtered.map(a => ({ id: a.id, name: a.name })).sort((a, b) => a.name.localeCompare(b.name));
    }, [areas, selectedClient]);

    const costCenterOptions = useMemo(() => {
        let filtered = costCenters.filter(cc => cc.is_active !== false);
        if (selectedClient.length > 0) {
            filtered = filtered.filter(cc => selectedClient.includes(cc.client_id));
        }
        return filtered.map(cc => ({ id: cc.id, name: cc.code + (cc.name ? ` - ${cc.name}` : '') })).sort((a, b) => a.name.localeCompare(b.name));
    }, [costCenters, selectedClient]);

    const requesterOptions = useMemo(() => {
        let filtered = requesters.filter(r => r.is_active !== false);
        if (selectedClient.length > 0) {
            filtered = filtered.filter(r => selectedClient.includes(r.client_id));
        }
        return filtered.map(r => ({
            id: r.id,
            name: r.full_name + (r.email ? ` (${r.email})` : '')
        })).sort((a, b) => a.name.localeCompare(b.name));
    }, [requesters, selectedClient]);

    // Estados de datos y paginación
    const [trips, setTrips] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [isLoading, setIsLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [isExportingExcel, setIsExportingExcel] = useState(false);

    // Modales de auditoría
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditTrip, setAuditTrip] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyTripId, setHistoryTripId] = useState(null);
    const [selectedTripLogs, setSelectedTripLogs] = useState([]);

    // Cargar catálogos
    useEffect(() => {
        const loadMasters = async () => {
            try {
                const [c, d, cc, ar, v, b, reqs] = await Promise.all([
                    getClients(),
                    getDrivers({ limit: 1000 }),
                    getCostCenters(),
                    getAreas(),
                    getVehicles({ limit: 1000 }),
                    getBases(),
                    getRequesters({ limit: 2000 }).catch(() => [])
                ]);
                setClients(c);
                setDrivers(d);
                setCostCenters(cc || []);
                setAreas(ar || []);
                setVehicles(v || []);
                setBases(b || []);
                setRequesters(reqs || []);
            } catch (err) {
                console.error("Error al cargar maestros para filtros:", err);
            }
        };
        loadMasters();
    }, []);

    const handleExportExcel = async () => {
        setIsExportingExcel(true);
        try {
            let start_date = null;
            let end_date = null;
            const isDirectLookup = !!tripIdFilter || !!osIdFilter;
            
            if (!isDirectLookup) {
                if (!customStart || !customEnd) {
                    toast.warning("Por favor, selecciona fecha de inicio y fin, o busca por ID de Reserva / OS para exportar.");
                    setIsExportingExcel(false);
                    return;
                }
                const start = new Date(customStart + 'T00:00:00');
                const end = new Date(customEnd + 'T23:59:59');
                const diffDays = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24));
                if (diffDays > 31) {
                    toast.error("No puedes exportar más de 31 días a la vez.");
                    setIsExportingExcel(false);
                    return;
                }
                start_date = start.toISOString();
                end_date = end.toISOString();
            }

            // Traer todos los viajes filtrados (sin límite de paginación de UI)
            const data = await globalSearchTrips({
                start_date,
                end_date,
                skip: 0,
                limit: 100000,
                client_id: selectedClient.length > 0 ? selectedClient.join(',') : null,
                driver_id: selectedDriver.length > 0 ? selectedDriver.join(',') : null,
                requester_id: selectedRequesters.length > 0 ? selectedRequesters.join(',') : null,
                status: filterStatus.length > 0 ? filterStatus.join(',') : null,
                cost_center_id: selectedCostCenters.length > 0 ? selectedCostCenters.join(',') : null,
                area_id: selectedAreas.length > 0 ? selectedAreas.join(',') : null,
                passenger_name: passengerName || null,
                vehicle_code: vehicleCode || null,
                search_term: searchTerm || null,
                trip_id: tripIdFilter ? parseInt(tripIdFilter) : null,
                os_id: osIdFilter || null,
                origin_base_id: selectedBases.length > 0 ? selectedBases.join(',') : null,
                country_code: selectedCountries.length > 0 ? selectedCountries.join(',') : null,
                file: fileFilter || null,
                billing_status: filterBillingStatus || null
            });

            const exportTrips = data.trips || [];
            if (exportTrips.length === 0) {
                toast.warning("No hay datos para exportar con los filtros seleccionados.");
                return;
            }

            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'Transportes Emilia SpA';
            workbook.created = new Date();

            // ==========================================
            // HOJA 1: SOLICITUDES (SÁBANA LIMPIA FILA 1)
            // ==========================================
            const sheet1 = workbook.addWorksheet('Solicitudes');
            sheet1.views = [{ showGridLines: true }];

            sheet1.columns = [
                { header: 'Nº Reserva', key: 'id', width: 14 },
                { header: 'ID Aquivoy', key: 'aquivoy_id', width: 14 },
                { header: 'Nº OS / Ref', key: 'ext_ref', width: 16 },
                { header: 'Fecha/Hora Local', key: 'local_time', width: 18 },
                { header: 'Fecha/Hora Chile', key: 'chile_time', width: 18 },
                { header: 'Zona Horaria', key: 'timezone', width: 22 },
                { header: 'Cliente / Empresa', key: 'client', width: 22 },
                { header: 'Pasajero Principal', key: 'passenger', width: 25 },
                { header: 'Solicitado Por', key: 'requester', width: 22 },
                { header: 'Cant. Pax', key: 'pax_count', width: 12 },
                { header: 'Detalle Pasajeros', key: 'pax_detail', width: 35 },
                { header: 'Nº Parada / Tramo', key: 'leg_label', width: 22 },
                { header: 'Pasajero Tramo / Parada', key: 'leg_passenger', width: 25 },
                { header: 'Teléfono Pasajero Tramo', key: 'leg_phone', width: 16 },
                { header: 'Origen (Pickup)', key: 'origin', width: 35 },
                { header: 'Destino (Dropoff)', key: 'destination', width: 35 },
                { header: 'Móvil', key: 'movil', width: 12 },
                { header: 'Conductor / Chofer', key: 'driver', width: 25 },
                { header: 'Patente', key: 'plate', width: 14 },
                { header: 'Fono Conductor', key: 'phone', width: 16 },
                { header: 'Tipo Vehículo', key: 'vehicle_type', width: 18 },
                { header: 'Código CC', key: 'cc_code', width: 14 },
                { header: 'Nombre CC', key: 'cc_name', width: 20 },
                { header: 'Área', key: 'area', width: 18 },
                { header: 'File / Expediente', key: 'file', width: 16 },
                { header: 'Nº Vuelo', key: 'flight', width: 14 },
                { header: 'Estado Servicio', key: 'status', width: 16 },
                { header: 'Estado Revisión', key: 'admin_status', width: 16 },
                { header: 'Moneda', key: 'currency', width: 12 },
                { header: 'Valor Servicio', key: 'base_price', width: 18 },
                { header: 'Espera (Min)', key: 'wait_min', width: 14 },
                { header: 'Valor Espera', key: 'wait_amount', width: 18 },
                { header: 'Monto Total Venta', key: 'total_amount', width: 20 },
                { header: 'Pago Chofer (Moneda Local)', key: 'driver_local', width: 24 },
                { header: 'Pago Chofer (CLP)', key: 'driver_clp', width: 20 },
                { header: 'Nº Liquidación', key: 'settlement', width: 16 },
                { header: 'Nº Prefactura', key: 'preinvoice', width: 16 },
                { header: 'Documentos Tributarios', key: 'docs', width: 25 },
                { header: 'Observaciones Operaciones', key: 'notes', width: 30 },
                { header: 'Observaciones Conductor', key: 'driver_notes', width: 30 }
            ];

            // Estilar Encabezado (Fila 1)
            const headerRow = sheet1.getRow(1);
            headerRow.height = 28;
            headerRow.eachCell((cell) => {
                cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFF' } };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: '1F4E78' } // Azul corporativo elegante
                };
                cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                cell.border = {
                    top: { style: 'thin', color: { argb: '000000' } },
                    bottom: { style: 'medium', color: { argb: '000000' } },
                    left: { style: 'thin', color: { argb: '1F4E78' } },
                    right: { style: 'thin', color: { argb: '1F4E78' } }
                };
            });

            // Llenar Filas de Datos
            let totalMontoSum = 0;
            let totalDriverCLPSum = 0;

            exportTrips.forEach((t) => {
                let tripTimezone = t.origin_base?.timezone || t.timezone;
                if (!tripTimezone) {
                    if (t.local_currency_code === 'PEN') tripTimezone = 'America/Lima';
                    else if (t.local_currency_code === 'ARS') tripTimezone = 'America/Argentina/Mendoza';
                    else if (t.pickup_lat < 0 && t.pickup_lat > -18 && t.pickup_lng < -68 && t.pickup_lng > -82) tripTimezone = 'America/Lima';
                    else tripTimezone = 'America/Santiago';
                }

                const localTimeStr = new Intl.DateTimeFormat('es-CL', {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                    timeZone: tripTimezone,
                    hour12: false
                }).format(new Date(t.scheduled_time));

                const chileTimeStr = new Intl.DateTimeFormat('es-CL', {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                    timeZone: 'America/Santiago',
                    hour12: false
                }).format(new Date(t.scheduled_time));

                const basePrice = Number(t.final_price || t.estimated_price || 0);
                const waitAmount = Number(t.waiting_time_amount || 0);
                const totalAmount = basePrice + waitAmount;

                const driverEarningsLocal = Number(t.driver_earnings || 0);
                const currencyCode = t.provider?.currency_code || t.driver?.provider?.currency_code || t.local_currency_code || 'CLP';
                
                let rate = t.exchange_rate_snapshot || 1;
                if (currencyCode === 'USD' && rate <= 1) {
                    rate = 925.0;
                } else if (currencyCode === 'PEN' && rate <= 1) {
                    rate = 280.0;
                } else if (currencyCode === 'ARS' && rate <= 1) {
                    rate = 0.6;
                }

                const driverEarningsCLP = currencyCode === 'CLP'
                    ? driverEarningsLocal
                    : (t.provider_cost_clp && Number(t.provider_cost_clp) > 0
                        ? Number(t.provider_cost_clp)
                        : Math.round(driverEarningsLocal * rate));

                totalMontoSum += totalAmount;
                totalDriverCLPSum += driverEarningsCLP;

                const sortedStops = t.stops && t.stops.length > 0
                    ? [...t.stops].sort((a, b) => (a.order || 0) - (b.order || 0))
                    : [];

                const passengerList = [];

                sortedStops.forEach((stop) => {
                    if (stop.address || stop.passenger_name) {
                        passengerList.push({
                            paxName: stop.passenger_name || '',
                            paxPhone: stop.passenger_phone || '',
                            pickupAddress: stop.address || t.pickup_address || '',
                            dropoffAddress: t.dropoff_address || ''
                        });
                    }
                });

                const isMainInStops = passengerList.some(p => 
                    (p.paxName && t.passenger_name && p.paxName.trim().toLowerCase() === t.passenger_name.trim().toLowerCase()) ||
                    (p.pickupAddress && t.pickup_address && p.pickupAddress.trim().toLowerCase() === t.pickup_address.trim().toLowerCase())
                );

                if (!isMainInStops && (t.passenger_name || t.pickup_address)) {
                    passengerList.unshift({
                        paxName: t.passenger_name || '',
                        paxPhone: t.passenger_phone || '',
                        pickupAddress: t.pickup_address || '',
                        dropoffAddress: t.dropoff_address || ''
                    });
                }

                if (passengerList.length === 0) {
                    passengerList.push({
                        paxName: t.passenger_name || '',
                        paxPhone: t.passenger_phone || '',
                        pickupAddress: t.pickup_address || '',
                        dropoffAddress: t.dropoff_address || ''
                    });
                }

                passengerList.forEach((pax, idx) => {
                    const isFirst = (idx === 0);
                    const legLabel = passengerList.length === 1
                        ? 'Directo'
                        : `Pasajero ${idx + 1} (Recogida ${idx + 1})`;

                    const addedRow = sheet1.addRow({
                        id: t.id,
                        aquivoy_id: t.aquivoy_id || '',
                        ext_ref: t.external_reference || '',
                        local_time: localTimeStr,
                        chile_time: chileTimeStr,
                        timezone: tripTimezone,
                        client: t.client?.name || '',
                        passenger: t.passenger_name || '',
                        requester: t.external_solicitante || t.requester?.full_name || '',
                        pax_count: 1 + (t.additional_passengers?.length || 0),
                        pax_detail: t.passenger_name + (t.additional_passengers && t.additional_passengers.length > 0 ? ' + ' + t.additional_passengers.map(p => p.full_name).join(', ') : ''),
                        leg_label: legLabel,
                        leg_passenger: pax.paxName,
                        leg_phone: pax.paxPhone,
                        origin: pax.pickupAddress,
                        destination: pax.dropoffAddress,
                        movil: t.vehicle?.internal_code || '',
                        driver: t.driver?.full_name || '',
                        plate: t.vehicle?.plate || t.driver?.vehicle_plate || '',
                        phone: t.driver?.phone || '',
                        vehicle_type: t.requested_vehicle_type?.name || '',
                        cc_code: t.cost_center?.code || t.cost_center_label || '',
                        cc_name: t.cost_center?.name || '',
                        area: t.area?.name || t.area_label || '',
                        file: t.file || '',
                        flight: t.flight_number || '',
                        status: STATUS_LABELS[t.status] || t.status || '',
                        admin_status: t.admin_status === 'processed' ? 'Procesado' : t.admin_status === 'rejected' ? 'Rechazado' : t.admin_status === 'settled' ? 'Pagado' : t.admin_status === 'pending_conciliation' ? 'Por Conciliar' : 'Por Revisar',
                        currency: currencyCode,
                        base_price: isFirst ? basePrice : 0,
                        wait_min: isFirst ? (t.waiting_time_minutes || 0) : 0,
                        wait_amount: isFirst ? waitAmount : 0,
                        total_amount: isFirst ? totalAmount : 0,
                        driver_local: isFirst ? driverEarningsLocal : 0,
                        driver_clp: isFirst ? driverEarningsCLP : 0,
                        settlement: t.settlement_id || t.historical_settlement_number || '',
                        preinvoice: t.client_invoice?.internal_number || t.historical_preinvoice_number || '',
                        docs: (t.client_invoice?.documents || []).map(d => `${d.type || 'FACTURA'} ${d.folio}`).join(', ') || t.historical_invoice_number || '',
                        notes: t.notes || '',
                        driver_notes: t.driver_notes || ''
                    });

                    addedRow.height = 20;

                    // Aplicar Bordes Finos y Alineación
                    addedRow.eachCell({ includeEmpty: true }, (cell) => {
                        cell.font = { name: 'Arial', size: 10 };
                        cell.border = {
                            top: { style: 'thin', color: { argb: 'D9D9D9' } },
                            bottom: { style: 'thin', color: { argb: 'D9D9D9' } },
                            left: { style: 'thin', color: { argb: 'D9D9D9' } },
                            right: { style: 'thin', color: { argb: 'D9D9D9' } }
                        };
                        cell.alignment = { vertical: 'middle' };
                    });

                    // Formato numérico de Moneda ($#,##0) con separadores de miles
                    addedRow.getCell('base_price').numFmt = '$#,##0';
                    addedRow.getCell('wait_amount').numFmt = '$#,##0';
                    addedRow.getCell('total_amount').numFmt = '$#,##0';
                    addedRow.getCell('driver_local').numFmt = '$#,##0';
                    addedRow.getCell('driver_clp').numFmt = '$#,##0';
                });
            });

            // Fila de Totales al final de la tabla
            const totalsRow = sheet1.addRow({
                id: 'TOTALES',
                total_amount: totalMontoSum,
                driver_clp: totalDriverCLPSum
            });
            totalsRow.height = 24;
            totalsRow.eachCell({ includeEmpty: true }, (cell) => {
                cell.font = { name: 'Arial', size: 11, bold: true };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'F2F4F7' }
                };
                cell.border = {
                    top: { style: 'medium', color: { argb: '1F4E78' } },
                    bottom: { style: 'double', color: { argb: '1F4E78' } }
                };
                cell.alignment = { vertical: 'middle' };
            });
            totalsRow.getCell('total_amount').numFmt = '$#,##0';
            totalsRow.getCell('driver_clp').numFmt = '$#,##0';

            // ==========================================
            // HOJA 2: RESUMEN EJECUTIVO (PESTAÑA SEPARADA CON LOGO)
            // ==========================================
            const sheet2 = workbook.addWorksheet('Resumen Ejecutivo');
            sheet2.views = [{ showGridLines: true }];

            // Cargar e incrustar el logo corporativo de Transportes Emilia
            try {
                const logoResponse = await fetch('/logoe.png');
                if (logoResponse.ok) {
                    const logoBuffer = await logoResponse.arrayBuffer();
                    const logoId = workbook.addImage({
                        buffer: logoBuffer,
                        extension: 'png'
                    });
                    sheet2.addImage(logoId, {
                        tl: { col: 0, row: 0 },
                        ext: { width: 160, height: 55 }
                    });
                }
            } catch (logoErr) {
                console.warn("Logo Transportes Emilia no pudo cargarse:", logoErr);
            }

            sheet2.addRow([]);
            sheet2.addRow([]);
            sheet2.addRow([]);
            sheet2.addRow(["REPORTE EJECUTIVO DE SOLICITUDES DE TRANSPORTE"]);
            sheet2.addRow([`Empresa: Transportes Emilia SpA | Fecha: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`]);
            sheet2.addRow([]);
            sheet2.addRow(["RESUMEN DE METRICAS OPERATIVAS"]);
            sheet2.addRow(["Métrica", "Valor"]);
            sheet2.addRow(["Total Solicitudes Exportadas", exportTrips.length]);
            sheet2.addRow(["Monto Total Venta", totalMontoSum]);
            sheet2.addRow(["Total Pago Choferes (CLP)", totalDriverCLPSum]);

            sheet2.getRow(4).font = { name: 'Arial', size: 14, bold: true, color: { argb: '1F4E78' } };
            sheet2.getRow(8).font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFF' } };
            sheet2.getRow(8).eachCell(cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1F4E78' } };
            });
            sheet2.getCell('B9').numFmt = '#,##0';
            sheet2.getCell('B10').numFmt = '$#,##0';
            sheet2.getCell('B11').numFmt = '$#,##0';
            sheet2.columns = [{ width: 34 }, { width: 25 }];

            // Generar buffer y descargar archivo
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const dateStr = format(new Date(), 'yyyyMMdd_HHmmss');
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `Reporte_Solicitudes_Emilia_${dateStr}.xlsx`;
            anchor.click();
            window.URL.revokeObjectURL(url);

            toast.success("Excel profesional de solicitudes exportado correctamente.");
        } catch (err) {
            console.error("Error al exportar solicitudes:", err);
            toast.error("Error al generar el archivo Excel.");
        } finally {
            setIsExportingExcel(false);
        }
    };

    // Función de búsqueda
    const performSearch = useCallback(async (targetPage = 1, size = pageSize) => {
        const isDirectLookup = !!tripIdFilter || !!osIdFilter;
        let start_date = null;
        let end_date = null;

        if (!isDirectLookup) {
            if (!customStart || !customEnd) {
                toast.warning("Por favor, selecciona fecha de inicio y fin, o busca por ID de Reserva / OS.");
                return;
            }

            const start = new Date(customStart + 'T00:00:00');
            const end = new Date(customEnd + 'T23:59:59');
            const diffDays = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24));

            if (diffDays > 31) {
                toast.error("No puedes filtrar por más de 31 días.");
                return;
            }
            start_date = start.toISOString();
            end_date = end.toISOString();
        }

        setIsLoading(true);
        setSearched(true);

        try {
            const skip = (targetPage - 1) * size;
            const data = await globalSearchTrips({
                start_date,
                end_date,
                skip,
                limit: size,
                client_id: selectedClient.length > 0 ? selectedClient.join(',') : null,
                driver_id: selectedDriver.length > 0 ? selectedDriver.join(',') : null,
                requester_id: selectedRequesters.length > 0 ? selectedRequesters.join(',') : null,
                status: filterStatus.length > 0 ? filterStatus.join(',') : null,
                cost_center_id: selectedCostCenters.length > 0 ? selectedCostCenters.join(',') : null,
                area_id: selectedAreas.length > 0 ? selectedAreas.join(',') : null,
                passenger_name: passengerName || null,
                vehicle_code: vehicleCode || null,
                search_term: searchTerm || null,
                trip_id: tripIdFilter ? parseInt(tripIdFilter) : null,
                os_id: osIdFilter || null,
                origin_base_id: selectedBases.length > 0 ? selectedBases.join(',') : null,
                country_code: selectedCountries.length > 0 ? selectedCountries.join(',') : null,
                file: fileFilter || null,
                billing_status: filterBillingStatus || null
            });

            setTrips(data.trips || []);
            setTotalCount(data.total || 0);
            setPage(targetPage);
        } catch (err) {
            console.error("Error al buscar viajes:", err);
            toast.error("Error al obtener resultados de la búsqueda.");
            setTrips([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    }, [customStart, customEnd, selectedClient, selectedDriver, selectedRequesters, filterStatus, selectedCostCenters, selectedAreas, passengerName, vehicleCode, searchTerm, tripIdFilter, osIdFilter, fileFilter, pageSize, selectedCountries, selectedBases, filterBillingStatus]);

    const handleSearchClick = (e) => {
        e.preventDefault();
        performSearch(1, pageSize);
    };

    const handleClearFilters = () => {
        setCustomStart('');
        setCustomEnd('');
        setTripIdFilter('');
        setOsIdFilter('');
        setSelectedClient([]);
        setSelectedDriver([]);
        setSelectedRequesters([]);
        setSelectedAreas([]);
        setSelectedCostCenters([]);
        setFilterStatus([]);
        setPassengerName('');
        setVehicleCode('');
        setFileFilter('');
        setSelectedCountries([]);
        setSelectedBases([]);
        setFilterBillingStatus('');
        setSearchTerm('');
        setTrips([]);
        setTotalCount(0);
        setSearched(false);
        setPage(1);
    };

    const hasActiveFilters = Boolean(
        customStart || customEnd || tripIdFilter || osIdFilter ||
        selectedClient.length > 0 || selectedDriver.length > 0 || selectedRequesters.length > 0 ||
        selectedAreas.length > 0 || selectedCostCenters.length > 0 || filterStatus.length > 0 ||
        passengerName || vehicleCode || fileFilter || selectedCountries.length > 0 ||
        selectedBases.length > 0 || filterBillingStatus || searchTerm
    );

    const handlePageChange = (newPage) => {
        performSearch(newPage, pageSize);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        performSearch(1, newSize);
    };

    const handleOpenAudit = (trip) => {
        setAuditTrip(trip);
        setShowAuditModal(true);
    };

    const handleShowHistory = async (tripId) => {
        setHistoryTripId(tripId);
        setShowHistoryModal(true);
        try {
            const logs = await getTripLogs(tripId);
            setSelectedTripLogs(logs || []);
        } catch (e) {
            console.error("Error al obtener logs:", e);
            setSelectedTripLogs([]);
        }
    };

    const getDriverName = (id) => drivers.find(dr => dr.id === id)?.full_name || '-';
    
    const getVehicleCode = (trip) => { 
        if (trip.vehicle?.internal_code) return trip.vehicle.internal_code; 
        if (trip.driver_id) {
            const v = vehicles.find(veh => veh.current_driver_id === trip.driver_id && veh.is_active);
            if (v) return v.internal_code;
        }
        return trip.driver?.vehicle_plate || '---'; 
    };

    // Opciones para filtros de selección
    const statusOptions = [
        { id: 'pending', name: 'Pendiente' },
        { id: 'assigned', name: 'Asignado' },
        { id: 'confirmed', name: 'Confirmado' },
        { id: 'en_route', name: 'En Camino' },
        { id: 'arrived', name: 'En Posición' },
        { id: 'on_board', name: 'En Ruta' },
        { id: 'completed', name: 'Completado' },
        { id: 'cancelled', name: 'Cancelado' },
        { id: 'no_show', name: 'No Show' },
        { id: 'anulado', name: 'Anulado' }
    ];

    return (
        <div className="p-3">
            {/* Tarjeta de Filtros */}
            <Card className="shadow-sm border-0 mb-3">
                <Card.Body className="p-3">
                    <Form onSubmit={handleSearchClick}>
                        {/* Fila 1: Fechas e Identificadores */}
                        <Row className="g-2">
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">Fecha Desde</Form.Label>
                                <Form.Control 
                                    type="date" 
                                    size="sm" 
                                    value={customStart} 
                                    onChange={e => setCustomStart(e.target.value)} 
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">Fecha Hasta</Form.Label>
                                <Form.Control 
                                    type="date" 
                                    size="sm" 
                                    value={customEnd} 
                                    onChange={e => setCustomEnd(e.target.value)} 
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">Nro. Reserva Emigo</Form.Label>
                                <Form.Control 
                                    type="number" 
                                    size="sm" 
                                    placeholder="Ej: 61213" 
                                    value={tripIdFilter} 
                                    onChange={e => setTripIdFilter(e.target.value)} 
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">Nro. OS</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    size="sm" 
                                    placeholder="Ej: OS-12345" 
                                    value={osIdFilter} 
                                    onChange={e => setOsIdFilter(e.target.value)} 
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">File</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    size="sm" 
                                    placeholder="Ej: F123..." 
                                    value={fileFilter} 
                                    onChange={e => setFileFilter(e.target.value)} 
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">Facturación</Form.Label>
                                <Form.Select 
                                    size="sm" 
                                    value={filterBillingStatus} 
                                    onChange={e => setFilterBillingStatus(e.target.value)}
                                    className={filterBillingStatus ? "fw-bold border-primary text-primary" : ""}
                                >
                                    <option value="">Todos...</option>
                                    <option value="not_preinvoiced">📄 No prefacturado</option>
                                    <option value="preinvoiced">⏳ Prefacturado</option>
                                    <option value="invoiced">✅ Facturado</option>
                                </Form.Select>
                            </Col>
                        </Row>

                        {/* Fila 2: Entidades y Personas */}
                        <Row className="g-2 mt-2">
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">Clientes</Form.Label>
                                <SearchableMultiSelect 
                                    options={clients.map(c => ({ id: c.id, name: c.name }))}
                                    selected={selectedClient}
                                    onChange={setSelectedClient}
                                    placeholder="Todos los clientes..."
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">Solicitante</Form.Label>
                                <SearchableMultiSelect 
                                    options={requesterOptions}
                                    selected={selectedRequesters}
                                    onChange={setSelectedRequesters}
                                    placeholder="Todos los solicitantes..."
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">Pasajero</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    size="sm" 
                                    placeholder="Nombre..." 
                                    value={passengerName} 
                                    onChange={e => setPassengerName(e.target.value)} 
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">Conductores</Form.Label>
                                <SearchableMultiSelect 
                                    options={drivers.map(d => {
                                        const v = vehicles.find(veh => veh.current_driver_id === d.id);
                                        return {
                                            id: d.id,
                                            name: v?.internal_code ? `${v.internal_code} - ${d.full_name || ''}` : (d.full_name || '')
                                        };
                                    })}
                                    selected={selectedDriver}
                                    onChange={setSelectedDriver}
                                    placeholder="Todos los conductores..."
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">Móvil</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    size="sm" 
                                    placeholder="Ej: 140..." 
                                    value={vehicleCode} 
                                    onChange={e => setVehicleCode(e.target.value)} 
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">Estado</Form.Label>
                                <SearchableMultiSelect 
                                    options={statusOptions}
                                    selected={filterStatus}
                                    onChange={setFilterStatus}
                                    placeholder="Estados..."
                                />
                            </Col>
                        </Row>

                        {/* Fila 3: Ubicación, Búsqueda Rápida y Acciones */}
                        <Row className="g-2 mt-2 align-items-end">
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">Área</Form.Label>
                                <SearchableMultiSelect 
                                    options={areaOptions}
                                    selected={selectedAreas}
                                    onChange={setSelectedAreas}
                                    placeholder="Todas las áreas..."
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">Centro de Costo</Form.Label>
                                <SearchableMultiSelect 
                                    options={costCenterOptions}
                                    selected={selectedCostCenters}
                                    onChange={setSelectedCostCenters}
                                    placeholder="Todos los CC..."
                                />
                            </Col>
                            <Col md={1}>
                                <Form.Label className="small text-muted mb-1 fw-bold">País</Form.Label>
                                <SearchableMultiSelect 
                                    options={countryOptions}
                                    selected={selectedCountries}
                                    onChange={setSelectedCountries}
                                    placeholder="País..."
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">Base</Form.Label>
                                <SearchableMultiSelect 
                                    options={baseOptions}
                                    selected={selectedBases}
                                    onChange={setSelectedBases}
                                    placeholder="Base..."
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small text-muted mb-1 fw-bold">Búsqueda Rápida</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    size="sm" 
                                    placeholder="Vuelo / Folio..." 
                                    value={searchTerm} 
                                    onChange={e => setSearchTerm(e.target.value)} 
                                />
                            </Col>
                            <Col md={3}>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="primary" 
                                        size="sm" 
                                        type="submit" 
                                        disabled={isLoading} 
                                        className="d-flex align-items-center justify-content-center gap-1 flex-fill"
                                    >
                                        {isLoading ? <Spinner size="sm" animation="border" /> : <Search size={14} />}
                                        Buscar
                                    </Button>
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm" 
                                        type="button"
                                        onClick={handleClearFilters}
                                        title="Limpiar todos los filtros"
                                        disabled={!hasActiveFilters}
                                        className="d-flex align-items-center justify-content-center gap-1"
                                    >
                                        <RotateCcw size={14} />
                                        Limpiar
                                    </Button>
                                    <Button 
                                        variant="success" 
                                        size="sm" 
                                        type="button"
                                        onClick={handleExportExcel} 
                                        disabled={isLoading || isExportingExcel}
                                        className="d-flex align-items-center justify-content-center gap-1 flex-fill"
                                    >
                                        {isExportingExcel ? <Spinner size="sm" animation="border" /> : '📥'}
                                        {isExportingExcel ? 'Exportando...' : 'Exportar Excel'}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* Listado de Solicitudes */}
            {!searched ? (
                <Alert variant="info" className="text-center py-5 border-0 shadow-sm">
                    <Calendar size={48} className="mb-3 text-info" />
                    <h5 className="fw-bold">Consulta de Solicitudes</h5>
                    <p className="text-muted mb-0">Selecciona un rango de fechas de hasta 31 días (o ingresa un ID de Reserva / Nro. OS) y aplica los filtros que requieras para buscar servicios en el sistema.</p>
                </Alert>
            ) : isLoading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" className="mb-2" />
                    <p className="text-muted">Cargando solicitudes desde el servidor...</p>
                </div>
            ) : trips.length === 0 ? (
                <Alert variant="warning" className="text-center py-5 border-0 shadow-sm">
                    <h5 className="fw-bold">Sin resultados</h5>
                    <p className="text-muted mb-0">No se encontraron servicios que coincidan con los filtros aplicados en el rango seleccionado.</p>
                </Alert>
            ) : (
                <Card className="shadow-sm border-0">
                    <Table hover responsive size="sm" className="align-middle small mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th style={{ width: '60px' }}>ID</th>
                                <th style={{ width: '110px' }}>Fecha/Hora</th>
                                <th style={{ width: '60px' }} className="text-center">Tipo</th>
                                <th>Cliente</th>
                                <th>Pasajero</th>
                                <th>Solicitante</th>
                                <th>Ruta (Origen / Destino)</th>
                                <th className="text-center" style={{ width: '60px' }}>T. Est</th>
                                <th>Estado</th>
                                <th className="text-end">Valor</th>
                                <th className="text-center">Móvil</th>
                                <th className="text-end">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {trips.map(t => {
                                const isVip = t.requested_vehicle_type?.name?.toLowerCase().includes('executive') || 
                                               t.requested_vehicle_type?.name?.toLowerCase().includes('luxury');

                                return (
                                    <tr key={t.id}>
                                        <td>
                                            <div className="d-flex align-items-center gap-1">
                                                <span className="fw-bold cursor-pointer text-primary" onClick={() => handleShowHistory(t.id)}>#{t.id}</span>
                                                {isVip && <span style={{ cursor: 'help' }} title="Servicio VIP">⭐</span>}
                                                {t.admin_status === 'processed' && (
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Validación OK</Tooltip>}>
                                                        <ShieldCheck size={14} className="text-success" />
                                                    </OverlayTrigger>
                                                )}
                                                <span className="cursor-pointer ms-1" onClick={() => handleOpenAudit(t)} title="Ver Mapa / GPS">🗺️</span>
                                            </div>
                                            {t.aquivoy_id && (
                                                <div className="bg-info bg-opacity-10 text-info fw-bold px-1 rounded border border-info border-opacity-25" style={{ fontSize: '0.8em', width: 'fit-content', marginTop: '2px' }}>AQ: {t.aquivoy_id}</div>
                                            )}
                                        </td>
                                        <td>
                                            {(() => {
                                                let tripTimezone = t.origin_base?.timezone || t.timezone;
                                                if (!tripTimezone) {
                                                    if (t.local_currency_code === 'PEN') tripTimezone = 'America/Lima';
                                                    else if (t.local_currency_code === 'ARS') tripTimezone = 'America/Argentina/Mendoza';
                                                    else if (t.local_currency_code === 'EUR') tripTimezone = 'Europe/Madrid';
                                                    else if (t.local_currency_code === 'UYU') tripTimezone = 'America/Montevideo';
                                                    else if (t.pickup_lat < 0 && t.pickup_lat > -18 && t.pickup_lng < -68 && t.pickup_lng > -82) tripTimezone = 'America/Lima';
                                                    else if (t.pickup_lat >= -35.5 && t.pickup_lat <= -30.0 && t.pickup_lng >= -58.5 && t.pickup_lng <= -53.0) tripTimezone = 'America/Montevideo';
                                                    else if (t.pickup_lat >= 36.0 && t.pickup_lat <= 44.0 && t.pickup_lng >= -9.5 && t.pickup_lng <= 4.5) tripTimezone = 'Europe/Madrid';
                                                    else tripTimezone = 'America/Santiago';
                                                }
                                                const isInternational = tripTimezone !== 'America/Santiago';
                                                const flag = getFlagEmoji(tripTimezone, t.local_currency_code, t.pickup_lat, t.pickup_lng, t.origin_base);
                                                if (isInternational) {
                                                    return (
                                                        <>
                                                            <div className="fw-bold text-primary">
                                                                {flag} {new Intl.DateTimeFormat('es-CL', {
                                                                    day: '2-digit', month: '2-digit', year: '2-digit',
                                                                    hour: '2-digit', minute: '2-digit',
                                                                    timeZone: tripTimezone,
                                                                    hour12: false
                                                                }).format(new Date(t.scheduled_time))} LOCAL
                                                            </div>
                                                            <div className="text-muted" style={{fontSize: '0.75em'}}>
                                                                {format(new Date(t.scheduled_time), 'HH:mm')} CHILE {format(new Date(t.scheduled_time), 'dd/MM/yy')}
                                                            </div>
                                                        </>
                                                    );
                                                } else {
                                                    return (
                                                        <>
                                                            <div className="fw-bold">{flag} {format(new Date(t.scheduled_time), 'HH:mm')}</div>
                                                            <div className="text-muted small">{format(new Date(t.scheduled_time), 'dd/MM/yy')}</div>
                                                        </>
                                                    );
                                                }
                                            })()}
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex align-items-center justify-content-center gap-1">
                                                <VehicleTypeIcon type={t.requested_vehicle_type} />
                                                <Badge bg="secondary" className="px-1 py-0.5" style={{ fontSize: '10px' }}>
                                                    x{(() => {
                                                        const uniqueStopsPax = t.stops?.filter(s => 
                                                            s.passenger_name && 
                                                            s.passenger_name.trim() !== '' && 
                                                            s.passenger_name !== t.passenger_name &&
                                                            !(t.additional_passengers?.some(ap => ap.full_name === s.passenger_name))
                                                        ).length || 0;
                                                        return Math.max(t.passenger_count || 1, 1 + (t.additional_passengers?.length || 0) + uniqueStopsPax);
                                                    })()}
                                                </Badge>
                                            </div>
                                        </td>
                                        
                                        {/* Columna Cliente con Centro de Costo abajo */}
                                        <td className="fw-bold text-dark">
                                            <div>{t.client?.name || t.client_label || '---'}</div>
                                            {t.cost_center && (
                                                <div className="text-muted fw-normal" style={{ fontSize: '0.72em', marginTop: '2px' }}>
                                                    🏢 {t.cost_center.name} {t.cost_center.code ? `(${t.cost_center.code})` : ''}
                                                </div>
                                            )}
                                        </td>
                                        
                                        <td>
                                            <div className="fw-bold">👤 {t.passenger_name}</div>
                                            <div className="text-primary small" style={{ fontSize: '0.85em' }}>{t.passenger_phone || 'Sin tel.'}</div>
                                        </td>
                                        <td>
                                            <div className="fw-bold">{t.external_solicitante || t.requester?.full_name || '---'}</div>
                                        </td>
                                        <td style={{ maxWidth: '250px' }}>
                                            <div className="small text-truncate" title={t.pickup_address}>
                                                <span className="text-success me-1">🟢</span>
                                                {isAirport(t.pickup_address) && <Plane size={12} className="me-1 text-info"/>}
                                                {t.pickup_address}
                                            </div>
                                            {t.stops && t.stops.length > 0 && (
                                                <div className="ps-2 my-1 border-start border-info text-muted small">
                                                    {t.stops.slice().sort((a,b) => a.order - b.order).map((stop, idx) => (
                                                        <div key={idx} className="text-truncate" title={stop.address}>
                                                            <span className="text-info me-1">📍</span>{stop.address}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="small text-truncate mt-1" title={t.dropoff_address}>
                                                <span className="text-danger me-1">🏁</span>
                                                {isAirport(t.dropoff_address) && <Plane size={12} className="me-1 text-info"/>}
                                                {t.dropoff_address}
                                            </div>
                                        </td>
                                        <td className="text-center" style={{ backgroundColor: 'rgba(13, 110, 253, 0.03)' }}>
                                            <div className="fw-bold text-primary">
                                                <Clock size={12} className="me-1"/>
                                                {t.estimated_duration_minutes || '-'}'
                                            </div>
                                            {t.distance_km > 0 && <div className="text-muted" style={{ fontSize: '0.7em' }}>{t.distance_km.toFixed(1)} km</div>}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <span 
                                                    style={{
                                                        width: '12px',
                                                        height: '12px',
                                                        borderRadius: '50%',
                                                        backgroundColor: getStatusColor(t.status, false),
                                                        display: 'inline-block'
                                                    }}
                                                ></span>
                                                <span className="fw-bold" style={{ color: getStatusColor(t.status, false), fontSize: '0.9rem' }}>
                                                    {STATUS_LABELS[t.status] || t.status}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="text-end fw-bold">
                                            ${(t.final_price || t.estimated_price || 0).toLocaleString('es-CL')}
                                            <div className="text-muted small" style={{ fontSize: '0.7em' }}>{t.local_currency_code || 'CLP'}</div>
                                        </td>
                                        <td className="text-center">
                                            {t.driver_id ? (
                                                <OverlayTrigger 
                                                    placement="top" 
                                                    overlay={
                                                        <Tooltip>
                                                            <div className="text-start">
                                                                <strong>Conductor:</strong> {getDriverName(t.driver_id)} <br/>
                                                                <strong>Pago:</strong> ${t.driver_earnings?.toLocaleString() || '0'} {t.provider_currency_code || t.local_currency_code || 'CLP'}
                                                            </div>
                                                        </Tooltip>
                                                    }
                                                >
                                                    <div className="fw-bold text-dark cursor-help">{getVehicleCode(t)}</div>
                                                </OverlayTrigger>
                                            ) : (
                                                <Badge bg="danger">Sin Asignar</Badge>
                                            )}
                                        </td>
                                        <td className="text-end">
                                            <Button variant="outline-primary" size="sm" onClick={() => handleShowHistory(t.id)}>
                                                Auditar
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                    
                    {/* Controles de Paginación del Servidor */}
                    <Card.Footer className="d-flex justify-content-between align-items-center bg-white border-top-0 pt-2">
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-muted small">Ver:</span>
                            <Form.Select 
                                size="sm" 
                                value={pageSize} 
                                onChange={e => handlePageSizeChange(parseInt(e.target.value))} 
                                style={{ width: '70px' }}
                            >
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </Form.Select>
                            <span className="text-muted small ms-2">Total: {totalCount} servicios</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                disabled={page === 1 || isLoading} 
                                onClick={() => handlePageChange(page - 1)}
                            >
                                &lt;
                            </Button>
                            <span className="small">Pág {page} de {Math.max(1, Math.ceil(totalCount / pageSize))}</span>
                            <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                disabled={page >= Math.ceil(totalCount / pageSize) || isLoading} 
                                onClick={() => handlePageChange(page + 1)}
                            >
                                &gt;
                            </Button>
                        </div>
                    </Card.Footer>
                </Card>
            )}

            {/* Modal de Auditoría Mapa / GPS */}
            <TripAuditModal 
                show={showAuditModal} 
                onHide={() => setShowAuditModal(false)} 
                trip={auditTrip} 
                isLoaded={isLoaded} 
            />

            {/* Modal de Historial de Auditoría / Cambios */}
            <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg" centered backdrop="static">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold text-dark">
                        📜 Historial de Auditoría - Reserva #{historyTripId}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                    {selectedTripLogs.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <Clock size={48} className="mb-3 text-secondary" />
                            <p className="mb-0">No se encontraron registros de auditoría para esta reserva.</p>
                        </div>
                    ) : (
                        <div className="position-relative ps-4 ms-2" style={{ borderLeft: '3px solid #dee2e6' }}>
                            {selectedTripLogs.map((log) => {
                                let badgeColor = 'bg-secondary';
                                let iconText = '📝';
                                const actionLower = log.action.toLowerCase();
                                
                                if (actionLower.includes('cread')) {
                                    badgeColor = 'bg-success text-white';
                                    iconText = '🌟';
                                } else if (actionLower.includes('rechaz')) {
                                    badgeColor = 'bg-danger text-white';
                                    iconText = '🚫';
                                } else if (actionLower.includes('actualiz') || actionLower.includes('modific')) {
                                    badgeColor = 'bg-primary text-white';
                                    iconText = '✏️';
                                } else if (actionLower.includes('evidenc') || actionLower.includes('foto')) {
                                    badgeColor = 'bg-warning text-dark';
                                    iconText = '📸';
                                } else if (actionLower.includes('parada')) {
                                    badgeColor = 'bg-info text-white';
                                    iconText = '📍';
                                } else if (actionLower.includes('cancel')) {
                                    badgeColor = 'bg-danger text-white';
                                    iconText = '❌';
                                }

                                const detailItems = log.details ? log.details.split(' | ') : [];

                                return (
                                    <div key={log.id} className="position-relative mb-4">
                                        <div 
                                            className={`position-absolute rounded-circle d-flex align-items-center justify-content-center ${badgeColor}`}
                                            style={{
                                                left: '-37px',
                                                top: '2px',
                                                width: '26px',
                                                height: '26px',
                                                fontSize: '0.85rem',
                                                border: '3px solid #f8f9fa',
                                                boxShadow: '0 0 0 1px #dee2e6',
                                                zIndex: 2
                                            }}
                                        >
                                            {iconText}
                                        </div>

                                        <Card className="border-0 shadow-sm">
                                            <Card.Body className="p-3">
                                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                                                    <div>
                                                        <span className="fw-bold text-dark">{log.action}</span>
                                                        <span className="text-muted small ms-2">por {log.user?.name || log.user?.email || 'Sistema'}</span>
                                                    </div>
                                                    <span className="text-muted small">{formatLogDate(log.created_at)}</span>
                                                </div>
                                                {detailItems.length > 0 && (
                                                    <ul className="mb-0 ps-3 text-muted small">
                                                        {detailItems.map((item, idx) => <li key={idx}>{item}</li>)}
                                                    </ul>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" size="sm" onClick={() => setShowHistoryModal(false)}>Cerrar</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default GlobalTripsView;
